import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Truck, ArrowLeft } from "lucide-react";

const LAST_UPDATED = "27 February 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-20 mb-10" data-testid={`privacy-section-${id}`}>
    <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
      {title}
    </h2>
    <div className="text-[15px] text-slate-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

const TOC = [
  ["overview", "1. Overview"],
  ["who-we-are", "2. Who we are"],
  ["accounts", "3. Customer and driver accounts"],
  ["data-we-collect", "4. The data we collect"],
  ["location", "5. Location and GPS"],
  ["live-tracking", "6. Live tracking"],
  ["push", "7. Push notifications"],
  ["biometric", "8. Biometric login"],
  ["camera-pod", "9. Camera and proof-of-delivery uploads"],
  ["payments", "10. Payment processing (Stripe)"],
  ["chat", "11. Messaging / chat"],
  ["cookies", "12. Cookies and local storage"],
  ["analytics", "13. Analytics"],
  ["third-parties", "14. Third-party processors"],
  ["retention", "15. How long we keep data"],
  ["rights", "16. Your rights (UK GDPR)"],
  ["deletion", "17. Account deletion and data requests"],
  ["security", "18. Security"],
  ["children", "19. Children"],
  ["changes", "20. Changes to this policy"],
  ["contact", "21. Contact"],
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Privacy Policy — Transport Anything 247</title>
        <meta
          name="description"
          content="Privacy policy for Transport Anything 247 — how we collect, use and protect data on the website, the Transport 247 Driver app and the Transport Anything 247 customer app."
        />
        <link rel="canonical" href="https://transportanything247.com/privacy" />
      </Helmet>

      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-back-home">
            <Truck className="h-7 w-7 text-orange-500" />
            <span className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>
              Transport Anything 247
            </span>
          </Link>
          <Link
            to="/"
            className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      {/* Title */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
            Privacy Policy
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Last updated: <span className="font-medium text-slate-700" data-testid="privacy-last-updated">{LAST_UPDATED}</span>
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* TOC */}
        <nav className="bg-white border border-slate-200 rounded-xl p-4 mb-10" data-testid="privacy-toc">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">On this page</p>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
            {TOC.map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="text-slate-700 hover:text-orange-600 hover:underline">
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <Section id="overview" title="1. Overview">
          <p>
            This Privacy Policy explains how Transport Anything 247 ("we", "us", "our") collects, uses
            and protects information about you when you use:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>the website at <span className="font-mono">transportanything247.com</span>;</li>
            <li>the <strong>Transport Anything 247</strong> customer mobile app (iOS and Android); and</li>
            <li>the <strong>Transport 247 Driver</strong> mobile app (iOS and Android).</li>
          </ul>
          <p>
            Together we refer to these as <strong>the Service</strong>. By using the Service you agree
            to this Policy. If you do not agree, please do not use the Service.
          </p>
          <p>
            We are committed to UK GDPR compliance (Data Protection Act 2018 and the UK General Data
            Protection Regulation). We collect only what we need to run a transport marketplace and
            never sell your personal information.
          </p>
        </Section>

        <Section id="who-we-are" title="2. Who we are">
          <p>
            Transport Anything 247 is a UK-based transport marketplace connecting customers with vetted
            drivers. For data protection purposes:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Data controller</strong>: Transport Anything 247.</li>
            <li><strong>Privacy contact</strong>: <a href="mailto:customers@transportanything247.com" className="text-orange-600 underline">customers@transportanything247.com</a>.</li>
            <li><strong>Registered jurisdiction</strong>: United Kingdom.</li>
          </ul>
        </Section>

        <Section id="accounts" title="3. Customer and driver accounts">
          <p>We operate two account types, each with its own data scope:</p>
          <p>
            <strong>Customer accounts</strong>: created when you book transport. We collect your name,
            email address, phone number, billing address and a hashed password (we never see or store
            your password in clear text). We use this information to authenticate you, deliver booking
            communications, send receipts and let you re-book past jobs.
          </p>
          <p>
            <strong>Driver accounts</strong>: created when a transport operator applies to the platform.
            In addition to the customer-account data above, drivers provide a UK driving licence number,
            vehicle insurance details, vehicle photos, and Stripe Connect onboarding information (handled
            by Stripe directly — we never store full bank-account or card details). We verify driver
            documents before approval and retain them for the lifetime of the driver relationship.
          </p>
        </Section>

        <Section id="data-we-collect" title="4. The data we collect">
          <p>We only collect what the Service genuinely uses. Specifically:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account data</strong>: name, email, phone, hashed password.</li>
            <li><strong>Booking data</strong>: pickup and delivery postcodes, item type, dimensions/weight, schedule, instructions you add to a booking.</li>
            <li><strong>Driver verification data</strong>: licence, insurance documents, vehicle photos and registration plate (drivers only).</li>
            <li><strong>Payment data</strong>: handled by Stripe — we receive only the last four digits of your card, the card brand and a payment-result token. We never see your full PAN, CVV or bank-account number.</li>
            <li><strong>Location data</strong>: see Section 5 — only collected when you are actively using the Service.</li>
            <li><strong>Proof-of-delivery photos</strong>: see Section 9 — uploaded by drivers at delivery.</li>
            <li><strong>Messages</strong>: in-app chat messages between customer, driver and our team (see Section 11).</li>
            <li><strong>Device identifiers</strong>: push tokens (drivers only), web-session identifiers, JWT subject identifiers — used to keep you signed in and deliver dispatch notifications.</li>
            <li><strong>Diagnostics</strong>: crash and performance logs that help us fix bugs. Not linked to your identity.</li>
            <li><strong>Operational telemetry</strong>: for drivers, we compute a private weekly efficiency / paid-mile / deadhead summary from your GPS breadcrumbs. This is visible only to you and to our internal operations team for support purposes.</li>
          </ul>
          <p className="bg-slate-100 rounded-lg p-3 text-[13px] text-slate-600">
            We do <strong>not</strong> collect: full payment-card numbers, biometric data (your fingerprint /
            Face ID stays on your device — see Section 8), advertising identifiers, or any data we then
            share with advertising networks.
          </p>
        </Section>

        <Section id="location" title="5. Location and GPS">
          <p><strong>Why we need it.</strong> Location data is essential to a transport marketplace:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><em>Customers</em>: to suggest your current location as a pickup point (foreground only, with your permission).</li>
            <li><em>Drivers</em>: to match you with nearby jobs, calculate accurate ETAs, and share your live location with the assigned customer during an active delivery.</li>
          </ul>
          <p><strong>Foreground location</strong> (both apps): we read your location only while the app is open.</p>
          <p>
            <strong>Background location</strong> (driver app only, and only while a job is active): we
            continue to capture position breadcrumbs when the screen is off so customers see accurate
            ETAs. You can revoke this permission at any time from your device's Settings; the driver
            app will fall back to foreground-only tracking.
          </p>
          <p>
            <strong>What we store.</strong> Breadcrumbs are recorded every ~30 seconds while you are
            online and are automatically deleted after 7 days. Aggregated derived data (miles driven,
            paid vs deadhead totals) is retained while your account is active.
          </p>
          <p>
            <strong>What we don't do.</strong> We do not track drivers when they are off-duty, do not
            share customer location with anyone other than the assigned driver, and do not sell or
            license location data to third parties.
          </p>
        </Section>

        <Section id="live-tracking" title="6. Live tracking">
          <p>
            Once a driver accepts a customer's booking, the customer can see the driver's live position
            on a map until delivery is confirmed. This sharing is automatic and tied to the booking; it
            ends when the booking status moves to "completed" or "cancelled". Customers see only their
            own assigned driver's location — never any other driver on the platform.
          </p>
        </Section>

        <Section id="push" title="7. Push notifications">
          <p>
            On native mobile apps we use the operating system's push services (Apple Push Notification
            service for iOS, Firebase Cloud Messaging for Android) to send job offers, status updates
            and booking confirmations. To do this we receive a <em>device push token</em> from Apple /
            Google and store it against your account.
          </p>
          <p>
            Push notifications are an essential part of the dispatch experience for drivers; the app
            will still function if you decline, but you may miss time-sensitive job offers. Customers
            can disable push notifications at any time from device Settings. We do not use push tokens
            for advertising.
          </p>
        </Section>

        <Section id="biometric" title="8. Biometric login">
          <p>
            On the driver app you can choose to unlock the app with Face ID, Touch ID or your Android
            fingerprint / face unlock. When you opt in:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your authentication token is stored in the device's secure store (iOS Keychain or Android Keystore).</li>
            <li>Your biometric data <strong>never leaves your device</strong> and is never sent to our servers — Apple and Google handle the biometric check locally.</li>
            <li>You can disable biometric unlock at any time from the driver app Profile screen, or by signing out.</li>
          </ul>
        </Section>

        <Section id="camera-pod" title="9. Camera and proof-of-delivery uploads">
          <p>
            The driver app uses the device camera to capture proof-of-delivery photos at the drop-off
            point. With the driver's permission, we also capture the GPS coordinates and an optional
            free-text note at the moment of upload, so the customer has a verifiable record.
          </p>
          <p>
            Proof-of-delivery photos are visible to (a) the assigned driver who took them, (b) the
            customer associated with the booking, and (c) Transport Anything 247 staff for support and
            dispute resolution. They are stored encrypted at rest and retained for 6 years after the
            booking as part of our commercial records (HMRC / contract dispute requirement).
          </p>
          <p>
            If you operate in patchy signal areas, the driver app may queue your POD upload locally on
            your device's IndexedDB store until the network reconnects, at which point it is sent and
            removed from the queue.
          </p>
        </Section>

        <Section id="payments" title="10. Payment processing (Stripe)">
          <p>
            We use <strong>Stripe</strong> as our sole payment processor:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Customer card payments are taken via <em>Stripe Checkout</em> and <em>Stripe Payment Intents</em>. Your card details are entered on Stripe-hosted UI and never touch our servers.</li>
            <li>Driver payouts are made through <em>Stripe Connect Express</em>. Drivers complete onboarding directly on Stripe's hosted forms; we receive only an account identifier and onboarding status.</li>
          </ul>
          <p>
            Stripe is an independent data controller for the personal data they process. Their privacy
            statement is at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">stripe.com/privacy</a>.
          </p>
        </Section>

        <Section id="chat" title="11. Messaging / chat">
          <p>
            The Service includes an in-app messaging channel between customers and the assigned driver,
            plus an "Operations" channel where you can contact our team. Message contents are stored
            for the lifetime of the related booking plus 12 months. Operations staff can view messages
            in the support context only.
          </p>
          <p>
            Please avoid sending sensitive personal information through chat — it is not designed to
            replace a secure channel for things like passport numbers or bank statements.
          </p>
        </Section>

        <Section id="cookies" title="12. Cookies and local storage">
          <p>
            The website and apps use only the storage we need to make the Service work. We do
            <strong> not</strong> use advertising cookies, fingerprinting trackers or cross-site
            tracking technologies on the Service. The detailed list (storage item, type, purpose
            and lifetime) is published in our separate <Link to="/cookies" className="text-orange-600 underline">Cookie Policy</Link>.
          </p>
        </Section>

        <Section id="analytics" title="13. Analytics">
          <p>
            We track diagnostic and performance data to operate the platform reliably: API response
            times, crash reports, and aggregated counts (e.g. how many drivers were online in a given
            week). This data is not used to profile individual users or for advertising.
          </p>
          <p>
            Drivers have access to a private "Your performance" widget on their dashboard. This shows
            their own miles, paid miles, deadhead miles, jobs completed and an efficiency percentage,
            for their eyes only. There is no public leaderboard and no comparison to other drivers.
          </p>
        </Section>

        <Section id="third-parties" title="14. Third-party processors">
          <p>
            We rely on the following processors to deliver the Service. They process data on our
            instructions only, under contractual data-protection terms.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Stripe, Inc.</strong> — payments and driver payouts.</li>
            <li><strong>Resend / GoDaddy SMTP</strong> — transactional email delivery (booking receipts, payout notifications, weekly digests).</li>
            <li><strong>Postcodes.io</strong> — UK postcode geocoding (we send only the postcode; never any account data).</li>
            <li><strong>Apple Push Notification service</strong> — iOS push delivery.</li>
            <li><strong>Google Firebase Cloud Messaging</strong> — Android push delivery.</li>
            <li><strong>OpenStreetMap tile providers</strong> — map tiles rendered in the live-tracking view.</li>
            <li><strong>Emergent (hosting) infrastructure</strong> — application hosting and MongoDB-backed primary storage.</li>
          </ul>
          <p>
            International transfers, where they occur, rely on Standard Contractual Clauses or
            equivalent UK-recognised safeguards.
          </p>
        </Section>

        <Section id="retention" title="15. How long we keep data">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account profile</strong>: kept while your account is active; deleted within 30 days of account closure (see Section 17).</li>
            <li><strong>Bookings, invoices, POD photos</strong>: retained for 6 years from booking date for HMRC and contract-dispute reasons.</li>
            <li><strong>Driver verification documents</strong>: retained for the lifetime of the driver relationship plus 6 years.</li>
            <li><strong>GPS breadcrumbs</strong>: 7 days; aggregated mileage summaries are retained while your account is active.</li>
            <li><strong>Chat messages</strong>: 12 months after the related booking is closed.</li>
            <li><strong>Diagnostic logs</strong>: 90 days.</li>
          </ul>
        </Section>

        <Section id="rights" title="16. Your rights (UK GDPR)">
          <p>Under UK GDPR you have the right to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>access the personal data we hold about you;</li>
            <li>request correction of inaccurate or incomplete data;</li>
            <li>request erasure of your personal data (subject to legal retention obligations);</li>
            <li>object to or restrict processing in certain circumstances;</li>
            <li>request portability — a machine-readable export of the data you have given us; and</li>
            <li>withdraw consent at any time for processing based on consent (e.g. push notifications).</li>
          </ul>
          <p>
            To exercise any right, email <a href="mailto:customers@transportanything247.com" className="text-orange-600 underline">customers@transportanything247.com</a>. We respond within one month.
            If you are dissatisfied with our response you can complain to the UK
            Information Commissioner's Office (ico.org.uk).
          </p>
        </Section>

        <Section id="deletion" title="17. Account deletion and data requests">
          <p>You can request deletion of your account in two ways:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>In-app</strong>: from the customer or driver Profile screen, choose "Delete account". You'll receive an email confirmation; once confirmed, we remove your account-identifiable data within 30 days.</li>
            <li><strong>By email</strong>: write to <a href="mailto:customers@transportanything247.com" className="text-orange-600 underline">customers@transportanything247.com</a> from the email address on the account.</li>
          </ul>
          <p>
            Some records — completed-booking invoices, driver verification documents, POD photos — must
            be retained for 6 years to comply with HMRC and commercial-contract obligations. After
            deletion those records are anonymised or pseudonymised so they are no longer linked to you.
          </p>
        </Section>

        <Section id="security" title="18. Security">
          <p>
            We protect personal data with industry-standard controls: TLS for all data in transit;
            encryption at rest in our primary MongoDB store; bcrypt-hashed passwords; JWT-signed
            authentication tokens with rotation; role-based access for our support team; and audited
            third-party services. No system is perfectly secure — we will notify you in line with UK
            GDPR Article 33 obligations should we ever suffer a breach affecting your personal data.
          </p>
        </Section>

        <Section id="children" title="19. Children">
          <p>
            The Service is intended for users aged 18 or over. We do not knowingly collect personal
            data from anyone under 18. If you believe a child has provided us with personal data,
            please contact <a href="mailto:customers@transportanything247.com" className="text-orange-600 underline">customers@transportanything247.com</a> and we will delete it promptly.
          </p>
        </Section>

        <Section id="changes" title="20. Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes to the Service or
            applicable law. We will revise the "Last updated" date at the top of this page and, for
            material changes, notify active users by email. Continued use of the Service after the
            effective date constitutes acceptance of the revised Policy.
          </p>
        </Section>

        <Section id="contact" title="21. Contact">
          <div className="bg-slate-900 text-white rounded-xl p-5">
            <p className="text-sm leading-relaxed">
              Questions, complaints or data-subject requests:
              <br />
              <a href="mailto:customers@transportanything247.com" className="text-orange-400 underline font-semibold">
                customers@transportanything247.com
              </a>
              <br />
              <span className="text-slate-300 text-xs">We respond within one working day for support enquiries and within one month for formal data-subject requests.</span>
            </p>
          </div>
        </Section>

        <p className="text-xs text-slate-500 mt-12 text-center">
          © {new Date().getFullYear()} Transport Anything 247. All rights reserved.
        </p>
      </main>
    </div>
  );
}
